import React from "react";
import {
  cn,
  extractLastFourDigitsFromUUID,
  getLayerNameByType,
  getShapeInfo,
} from "../../../lib/utils";
import { ChevronRight, LucideIcon } from "lucide-react";
import { GroupIcon } from "@radix-ui/react-icons";
import * as Collapsible from "@radix-ui/react-collapsible";
import * as Switch from "@radix-ui/react-switch";
import { LockKeyhole } from "lucide-react";
import {
  getConsumerLockMenuTitle,
  getConsumerLockMenuType,
} from "../../../constants";
import LayerItemName from "./layer-item-name";
import LayerItemTranslations from "./layer-item-translation";

const LayerItem = ({
  shape,
  depth = 0, // Default depth is 0
  locksMenuOpen,
  handleLocksMenuOpen,
  handleLocksMenuClose,
  selectedCanvasObject, // this is the selected canvas object from internal state for locks menu.
  setSelectedCanvasObject,
  handleLocksMenuValueChange,
  selectedElementAttributes, // global selected element attributes.
  onLayerItemSelect,
  parentGroupObjectId = null, // when id is passed, means this subitem is inside a group.
  // we need to pass the parent id to most functions to have the logic identify the group faster.

  handleLayerNameChange,
  isGroup,
  isCollapsed,
  toggleGroupCollapse,
  sidebarWidth,
}: any) => {
  // const isGroup = shape?.type === "group";

  const info = getShapeInfo(
    shape?.type === "i-text" && shape?.isCurvedText
      ? "curved-text"
      : shape?.type
  );
  const IconComponent = info.icon as LucideIcon;

  const isOpen =
    locksMenuOpen && selectedCanvasObject?.objectId === shape?.objectId;

  const handleSwitchToggle = (key: string, value: boolean) => {
    const currentLocks = { ...selectedCanvasObject.consumerLocks };

    // with the all functionality, we need to toggle all the locks off or on, and
    // if they all become true, we need to toggle the all switch to true.
    // if they all become false, we need to toggle the all switch to false.

    const finalShapeType =
      shape.type === "i-text" && shape.isCurvedText
        ? "curved-text"
        : shape.type;

    if (key === "all") {
      console.log("all switch toggled");
      Object.keys(currentLocks).forEach((lockKey) => {
        if (getConsumerLockMenuType(finalShapeType, lockKey) === "boolean")
          currentLocks[lockKey] = value;
      });
    } else {
      currentLocks[key] = value;
    }

    // check if all the locks are true, then toggle the all switch to true,
    // except the all switch itself.
    const allLocksTrue = Object.keys(currentLocks)
      .filter(
        (lockKey) =>
          lockKey !== "all" &&
          getConsumerLockMenuType(finalShapeType, lockKey) === "boolean"
      )
      .every((lockKey) => currentLocks[lockKey] === true);

    if (allLocksTrue) {
      currentLocks["all"] = true;
    } else {
      currentLocks["all"] = false;
    }

    // call the handle locks menu value change.
    handleLocksMenuValueChange(currentLocks);
  };

  const handleNumberBasedLockChange = (key: string, value: number) => {
    if (isNaN(value) || value < 0) return;

    const currentLocks = { ...selectedCanvasObject.consumerLocks };

    currentLocks[key] = value;

    handleLocksMenuValueChange(currentLocks);
  };

  // determine if this layerItem is the selected layerItem or not.

  const isSelectedLayerItem = () => {
    // if is active selection, for now return false, will implement later.
    if (selectedElementAttributes.type === "activeSelection") return false;

    // if the selected element is a single object.
    return selectedElementAttributes.objectId === shape.objectId;
  };

  const isSelectedWithinActiveSelection = () => {
    // if is active selection, for now return false, will implement later.
    if (selectedElementAttributes.type !== "activeSelection") return false;

    // check inside attributes, if objectIds have this item id.

    const ids = selectedElementAttributes.objectIds;
    return ids.includes(shape.objectId);
  };

  const getDefaultLayerNameFromShape = (shape: any) => {
    // new shapes will have a default name based on the type.
    // to handle non-existent layer names in old shapes. Populate them based on type.

    if (!shape?.layerName) {
      // handle curved text.
      const type =
        shape?.type === "i-text" && shape?.isCurvedText
          ? "curved-text"
          : shape?.type;

      return getLayerNameByType(type);
    }

    return shape?.layerName;
  };

  return (
    <Collapsible.Root className="w-full" open={isOpen}>
      <div
        key={shape?.objectId}
        className={cn(
          "group w-full my-1 flex items-center gap-2 /px-2 py-2.5 hover:cursor-pointer border border-transparent hover:border hover:border-primary",

          { "bg-primary": isSelectedLayerItem() }, // single item selected
          { "bg-black": isSelectedWithinActiveSelection() }, // active selection.
          { "bg-blackLight": parentGroupObjectId } // group subitem.
        )}
        style={{
          paddingRight: "3px",
          paddingLeft: `${8 + depth * 22}px`,
        }}
      >
        <div className="flex items-center gap-x-2 flex-1">
          {isGroup && (
            <ChevronRight
              onClick={() => {
                console.log("toggle group collapse", shape?.objectId);
                isGroup && toggleGroupCollapse?.(shape.objectId);
              }}
              size={16}
              className={cn("text-white transition-transform", {
                "rotate-90": !isCollapsed,
              })}
            />
          )}

          <LockKeyhole
            size={16}
            className={cn(`text-gray-400 hover:text-white me-1`, {
              "text-white": isOpen,
            })}
            onClick={
              isOpen
                ? () => handleLocksMenuClose()
                : () => handleLocksMenuOpen(shape)
            }
          />

          <IconComponent size={16} />

          <LayerItemTranslations
            sidebarWidth={sidebarWidth} // Pass the sidebar
            layerId={shape.objectId}
            layerName={shape.layerName}
            onLayerNameChange={handleLayerNameChange}
          />
        </div>

        <div className="flex items-center self-center justify-self-end">
          <span title={shape?.objectId} className="text-[10px] text-bodyDark">
            #{extractLastFourDigitsFromUUID(shape?.objectId)}
          </span>

          <button
            onClick={() => {
              onLayerItemSelect(shape.objectId);
            }}
            disabled={isSelectedLayerItem() || !!parentGroupObjectId}
            className=" hover:opacity-100 mx-2"
          >
            <GroupIcon
              fontSize={18}
              className={cn("text-white opacity-0", {
                "group-hover:opacity-100":
                  !isSelectedLayerItem() && !!!parentGroupObjectId,
              })}
            />
          </button>
        </div>
      </div>

      <Collapsible.Content className="">
        <div className="w-full h-full bg-black flex flex-col gap-y-1  justify-center py-2.5 px-5 text-sm">
          <div className="flex gap-x-1 items-center justify-start mb-1">
            <LockKeyhole className="text-white" size={15} />
            <span>Consumer Locks</span>
          </div>

          {selectedCanvasObject &&
            Object.entries(selectedCanvasObject.consumerLocks).map(
              ([key, value]) => {
                const finalShapeType =
                  shape.type === "i-text" && shape.isCurvedText
                    ? "curved-text"
                    : shape.type;

                const title = getConsumerLockMenuTitle(finalShapeType, key);

                const type = getConsumerLockMenuType(finalShapeType, key);

                if (!title || !type || !finalShapeType) return null;

                return (
                  <div
                    key={key}
                    className="items-center gap-x-1 grid grid-cols-2"
                  >
                    <span className="text-white">{title}: </span>

                    {type === "boolean" && (
                      <Switch.Root
                        checked={(value as boolean) ?? false}
                        onCheckedChange={(e) => handleSwitchToggle(key, e)}
                        className="bg-borderColor hover:cursor-pointer w-[32px] h-[15px] rounded-full relative focus:shadow-black data-[state=checked]:bg-primary outline-none cursor-default"
                        id="airplane-mode"
                      >
                        <Switch.Thumb className="block w-[15px] h-[12px] bg-white rounded-full shadow-[0_2px_2px] shadow-blackA4 transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[19px]" />
                      </Switch.Root>
                    )}

                    {type === "number" && (
                      <input
                        type="number"
                        className="bg-borderColor w-[65px] h-[35px] rounded-md text-white text-sm text-center"
                        value={value as number}
                        onChange={(e) =>
                          handleNumberBasedLockChange(
                            key,
                            Number(e.target.value)
                          )
                        }
                      />
                    )}
                  </div>
                );
              }
            )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export default LayerItem;
